import { RadioOption, RadioOptionGroup, Spinner } from '@wundo/ui';
import type { NotificationMeeting } from '@/lib/notificationSend.ts';
import { formatDateTime } from '@/utils/dateUtils.ts';
import { titleOf } from './sendLabels.ts';

/* The three facts the timer itself checks before deciding a meeting moment is due. */
function meetingFacts(meeting: NotificationMeeting): string {
  const facts = [
    meeting.over ? 'Over' : null,
    meeting.has_recording ? 'Recording' : null,
    meeting.has_summary ? 'Report' : null,
  ].filter(Boolean);

  return [formatDateTime(meeting.starts_at), ...facts].join(' · ');
}

interface MeetingChoiceProps {
  loading: boolean;
  meetings: NotificationMeeting[];
  value: string;
  onChange: (meetingId: string) => void;
}

export function MeetingChoice(props: MeetingChoiceProps) {
  return (
    <div className='flex flex-col gap-2'>
      <h3 className='text-sm font-bold text-ink'>Meeting</h3>
      <MeetingOptions {...props} />
    </div>
  );
}

function MeetingOptions({
  loading,
  meetings: options,
  value,
  onChange,
}: MeetingChoiceProps) {
  if (loading) {
    return (
      <p role='status' className='flex items-center gap-2 text-sm text-ink-muted'>
        <Spinner className='size-4' />
        Loading meetings...
      </p>
    );
  }
  if (options.length === 0) {
    return (
      <p className='text-sm text-ink-muted'>
        This person has no meetings in the past month or the next two weeks.
      </p>
    );
  }
  return (
    <div className='max-h-96 overflow-y-auto'>
      <RadioOptionGroup
        aria-label='Meeting'
        value={value}
        onValueChange={(picked) => onChange(String(picked))}
      >
        {options.map((meeting) => (
          <RadioOption
            key={meeting.id}
            value={meeting.id}
            label={titleOf(meeting)}
            description={meetingFacts(meeting)}
          />
        ))}
      </RadioOptionGroup>
    </div>
  );
}
